import { useState, useEffect } from 'react';

interface EngagementConfig {
    timeThreshold?: number; // en ms (ex: 45000 pour 45s)
    scrollThreshold?: number; // en % (ex: 50 pour 50%)
    coolingPeriodDays?: number; // jours avant de réafficher après fermeture (ex: 3)
}

export const useSmartEngagement = (config: EngagementConfig = {}) => {
    const {
        timeThreshold = 45000,
        scrollThreshold = 50,
        coolingPeriodDays = 3
    } = config;

    const [shouldShow, setShouldShow] = useState(false);
    const [debugReason, setDebugReason] = useState<string | null>(null);

    useEffect(() => {
        // 1. Vérification du délai de refroidissement (Cooling Period)
        const dismissedUntil = localStorage.getItem('bmc-dismissed-until');
        if (dismissedUntil && new Date().getTime() < parseInt(dismissedUntil)) {
            return; // Ne rien faire si on est dans la période de silence
        }

        // 2. Vérification du nombre de visites (Fidélité)
        // On considère qu'un utilisateur est "engagé" s'il revient pour une 2ème session
        // La visite courante est incrémentée dans App.tsx ou un contexte global, 
        // ici on lit juste la valeur.
        const visitCount = parseInt(localStorage.getItem('visit_count') || '1');
        if (visitCount >= 2) {
            setShouldShow(true);
            setDebugReason('Returning visitor');
            return;
        }

        // 3. Trigger Temps (Timer)
        const timer = setTimeout(() => {
            setShouldShow(true);
            setDebugReason('Time threshold reached');
        }, timeThreshold);

        // 4. Trigger Scroll
        const handleScroll = () => {
            if (shouldShow) return; // Déjà affiché

            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = (scrollTop / docHeight) * 100;

            if (scrollPercent > scrollThreshold) {
                setShouldShow(true);
                setDebugReason('Scroll threshold reached');
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [timeThreshold, scrollThreshold, shouldShow]);

    // Fonction pour masquer (avec délai de refroidissement)
    const dismiss = () => {
        const until = new Date();
        until.setDate(until.getDate() + coolingPeriodDays);
        localStorage.setItem('bmc-dismissed-until', until.getTime().toString());
        setShouldShow(false);
    };

    return { shouldShow, dismiss, debugReason };
};

